<template>
    <div class="s2w-range-selector-container">
        <!-- eslint-disable vue/no-v-html -->
        <div
            v-if="service.rangeSelector.warning"
            class="message-tip"
            v-html="service.rangeSelector.warning"
        />

        <div class="s2w-range-selector">
            <div class="selector-label">
                {{ $t('BaseSaveToWorkspace.RangeSelector.title') }}
            </div>
            <div class="range-function-pane">
                <template v-if="service.enableRange">
                    <div class="use-select">
                        <div
                            class="s2w-range-radio"
                            :class="{
                                disabled: service.disabledAllType,
                                active: service.rangeSelector.selectedType === 'ALL'
                            }"
                            v-bind="disabledTooltipOption"
                            @click="changeRangeType('ALL', service.disabledAllType)"
                        >
                            <div class="s2w-range-radio__dot" />
                            <div
                                class="s2w-range-radio__content"
                            >
                                <span v-html="selectAllLabel" />
                                <template v-if="allGroupCountLoading">
                                    <PtIcon
                                        v-if="allGroupCountLoading"
                                        icon="SolidLoadingBars"
                                        style="font-size: 24px; margin-left: 4px"
                                    />
                                </template>
                                <template v-else>
                                    <span
                                        v-if="familyMode && groupMode && allGroupCounts"
                                        :class="service.disabledAllType ? 's2w-range-radio__content__disabled' : 's2w-range-radio__content__counts'"
                                        v-html="$t('BaseSaveToWorkspace.RangeSelector.selectAllFamilyPatentCountLabel',
                                                   { count: service.formatNumber(allGroupCounts) })"
                                    />
                                </template>
                            </div>
                        </div>
                        <div class="range-container">
                            <div
                                class="s2w-range-radio no-margin"
                                :class="{
                                    disabled: service.disabledRangeType,
                                    active: service.rangeSelector.selectedType === 'RANGE'
                                }"
                                @click="changeRangeType('RANGE', service.disabledAllType)"
                            >
                                <div class="s2w-range-radio__dot" />
                                <div class="s2w-range-radio__content">
                                    <div
                                        class="range-selector"
                                        :class="{ 'is-error': service.rangeSelector.error }"
                                    >
                                        <span class="range-label">
                                            {{ $t('BaseSaveToWorkspace.RangeSelector.from') }}
                                        </span>
                                        <input
                                            class="range-input"
                                            :value="service.rangeSelector.from"
                                            :disabled="service.disabledRangeType"
                                            @blur="onRangeInputBlur($event, 'from')"
                                        >
                                        <span class="range-label">
                                            {{ $t('BaseSaveToWorkspace.RangeSelector.to') }}
                                        </span>
                                        <input
                                            class="range-input"
                                            :value="service.rangeSelector.to"
                                            :disabled="service.disabledRangeType"
                                            @blur="onRangeInputBlur($event, 'to')"
                                        >
                                    </div>
                                    <div
                                        v-if="familyMode && groupMode"
                                        class="range-count"
                                    >
                                        <template v-if="familyRangeCountLoading">
                                            <PtIcon
                                                v-if="familyRangeCountLoading"
                                                icon="SolidLoadingBars"
                                                style="font-size: 24px;"
                                            />
                                        </template>
                                        <template v-else>
                                            {{ $t('BaseSaveToWorkspace.RangeSelector.familyLabel',
                                                  { count: service.formatNumber(familyRangeCount)}) }}
                                        </template>
                                    </div>
                                </div>
                            </div>
                            <div
                                class="range-error"
                            >
                                {{ service.rangeSelector.error }}
                            </div>
                        </div>
                        <div
                            v-if="showRangeTip"
                            class="range-info"
                            :aria-label="$t('BaseSaveToWorkspace.RangeSelector.tip')"
                            data-balloon-pos="up"
                            data-balloon-white
                        >
                            <PtIcon
                                icon="SolidInfo"
                                style="font-size: 24px;"
                            />
                        </div>
                        <!-- <div
                            v-if="familyMode && groupMode"
                            class="range-count"
                        >
                            <template v-if="familyRangeCountLoading">
                                <PtIcon
                                    v-if="familyRangeCountLoading"
                                    icon="SolidLoadingBars"
                                    style="font-size: 24px;"
                                />
                            </template>
                            <template v-else>
                                {{ $t('BaseSaveToWorkspace.RangeSelector.familyLabel',
                                      { count: service.formatNumber(familyRangeCount)}) }}
                            </template>
                        </div> -->
                    </div>
                </template>
                <template v-else>
                    <div class="use-simple">
                        <slot
                            name="simple"
                            :allCounts="allCounts"
                        >
                            <!-- eslint-disable vue/no-v-html -->
                            <span v-html="selectLabel" />
                        </slot>
                    </div>
                </template>

                <FamilySelector v-if="enableFamily" />
            </div>
        </div>
    </div>
</template>

<script>
import PtIcon from '@patsnap-biz/icon';
import '@patsnap-ui/icon/assets/solid/info.svg';
import '@patsnap-ui/icon/assets/solid/LoadingBars.svg';
import isNaN from 'lodash/isNaN';
import FamilySelector from './FamilySelector';

export default {
    name: 'SaveToWorkspaceRangeSelector',
    components: {
        FamilySelector,
        PtIcon,
    },
    inject: ['service'],
    computed: {
        disabledTooltipOption() {
            const tooltip = this.service.getDisabledAllTypeTooltip();
            if (!tooltip) return {};
            return {
                'aria-label': tooltip,
                'data-balloon-pos': 'up',
                'data-balloon-white': true,
            };
        },
        allCounts() {
            return this.service.rangeSelector.allCounts;
        },
        groupCounts() {
            return this.service.rangeSelector.groupCounts;
        },
        familyMode() {
            return this.service.familyMode;
        },
        groupMode() {
            return this.service.groupMode;
        },
        selectedGroupCounts() {
            return this.service.searchGroupMode.selectedGroupCounts;
        },
        allGroupCounts() {
            return this.service.allGroupCounts;
        },
        showRangeTip() {
            return this.service.showRangeTip && !this.familyMode;
        },
        familyRangeCount() {
            return this.service.familyRangeCount;
        },
        familyRangeCountLoading() {
            return this.service.familyRangeCountLoading;
        },
        allGroupCountLoading() {
            return this.service.allGroupCountLoading;
        },
        enableFamily() {
            if (this.familyMode) {
                return this.service.enableFamily && !this.groupMode;
            }
            return this.service.enableFamily;
        },
        selectAllLabel() {
            const { formatNumber } = this.service;
            if (this.familyMode && this.groupMode) {
                return this.$t('BaseSaveToWorkspace.RangeSelector.selectAllFamilyLabel',
                    { allCounts: formatNumber(this.groupCounts) });
            }
            return `${this.$t('BaseSaveToWorkspace.RangeSelector.selectAllLabel')} (${formatNumber(this.allCounts)})`;
        },
        selectLabel() {
            const { formatNumber } = this.service;
            if (this.familyMode && this.groupMode) {
                return this.$t('BaseSaveToWorkspace.RangeSelector.selectFamilyLabel',
                    { allCounts: formatNumber(this.allCounts), count: formatNumber(this.selectedGroupCounts) });
            }
            return this.$t(`BaseSaveToWorkspace.${this.service.type}.RangeSelector.simpleSelectLabel`, this.allCounts,
                { count: formatNumber(this.allCounts) });
        },
    },
    watch: {
        'service.rangeSelector.selectedType': function watchSelectedTypeChange(selectedType) {
            const { enableAutoUpdate, rangeSelector } = this.service;
            if (selectedType === 'RANGE') {
                if (rangeSelector && rangeSelector.family) {
                    rangeSelector.family.enable = false;
                }
            }
            if (enableAutoUpdate && this.service.identifyAutoUpdateStatus) {
                this.service.identifyAutoUpdateStatus();
            }
        },
    },
    methods: {
        changeRangeType(type, disabled) {
            if (disabled) return false;
            this.service.rangeSelector.selectedType = type;
            return true;
        },
        onRangeInputBlur(e, key) {
            if (!e || !e.target) return;
            const { value } = e.target;

            if (!value) {
                this.service.rangeSelector[key] = '';
                e.target.value = this.service.rangeSelector[key];
                return;
            }

            const newValue = parseInt(value, 10);

            if (!isNaN(newValue)) {
                const { min } = this.service.rangeSelector;
                this.service.rangeSelector[key] = Math.max(min, newValue);
            }
            e.target.value = this.service.rangeSelector[key];

            this.service.getFamilyRangeCount();
        },
    },
};
</script>
